import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, RefreshCw, XCircle } from "lucide-react";
import { singleFlight } from "./liveChatModel";
import {
  type ActionButtonResponse,
  type ActionCard,
  type ActionResultItem,
  type ButtonKind,
  type CardState,
  actionLabel,
  confirmLabel,
  countdownAnnouncement,
  formatBRL,
  formatCountdown,
  formatDate,
  formatPrevisao,
  reduceCardResponse,
  secondsLeft,
  statusLabel,
} from "./payablesActionViewModel";

/**
 * Story SF-CHAT-8.2 — cartão de prévia de uma ação sobre contas a pagar.
 *
 * Mostra exatamente o que vai mudar e só executa pelo botão Confirmar, com um
 * grant novo a cada clique (quem chama `onAction` o pede). Um clique por vez:
 * a trava de uso único descarta o segundo enquanto o primeiro está em voo.
 */

interface Props {
  card: ActionCard;
  onAction: (kind: ButtonKind, previewId: string) => Promise<ActionButtonResponse>;
}

const FINAL_TEXT: Partial<Record<CardState, string>> = {
  cancelled: "Prévia cancelada. Nada foi alterado.",
  expired: "A prévia expirou e nada foi alterado. Refaça a prévia para continuar.",
  unavailable: "Ações pelo chat estão desligadas no aplicativo agora. Nada foi alterado.",
};

export function PayablesActionCard({ card: initial, onAction }: Props) {
  const [card, setCard] = useState(initial);
  const [state, setState] = useState<CardState>("open");
  const [busy, setBusy] = useState<ButtonKind | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResultItem[] | null>(null);
  const [left, setLeft] = useState(() => secondsLeft(initial.expiresAt, Date.now()));
  const [announcement, setAnnouncement] = useState("");
  const previous = useRef(left);
  const desfecho = useRef<HTMLDivElement>(null);
  const estadoAnterior = useRef<CardState>("open");

  // Revisão da 8.2: o botão focado some quando a prévia termina; o foco vai
  // para o desfecho, em vez de cair no `body`.
  useEffect(() => {
    if (estadoAnterior.current !== state && state !== "open") desfecho.current?.focus();
    estadoAnterior.current = state;
  }, [state]);
  const titleId = `acao-${card.previewId}`;

  // Contagem regressiva só enquanto a prévia pode ser confirmada.
  useEffect(() => {
    if (state !== "open") return;
    const tick = () => {
      const agora = secondsLeft(card.expiresAt, Date.now());
      const aviso = countdownAnnouncement(previous.current, agora);
      previous.current = agora;
      setLeft(agora);
      if (aviso) setAnnouncement(aviso);
      if (agora === 0) setState("expired");
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [card.expiresAt, state]);

  const run = useMemo(
    () =>
      singleFlight(async (kind: ButtonKind) => {
        setBusy(kind);
        setNotice(null);
        try {
          const r = await onAction(kind, card.previewId);
          const update = reduceCardResponse(kind, r);
          if (update.card) {
            previous.current = secondsLeft(update.card.expiresAt, Date.now());
            setCard(update.card);
          }
          setResult(update.result);
          setNotice(update.notice);
          setState(update.state);
        } catch (erro) {
          // Grant recusado, backend fora do ar: a prévia continua valendo.
          setNotice(erro instanceof Error ? erro.message : "Não foi possível concluir. Nada foi alterado.");
        } finally {
          setBusy(null);
        }
      }),
    [card.previewId, onAction],
  );

  const aberto = state === "open";
  const desabilitado = busy !== null;
  const plural = card.quantidade === 1 ? "conta" : "contas";
  const agendar = card.action === "agendar_pagamento";
  // Story 8.3: no agendar, o "depois" é a previsão gravada; nas outras, a situação.
  const finalPorId = new Map(
    (result ?? []).map((i) => [i.id, agendar ? i.data_previsao_pagamento : i.status_validacao]),
  );

  const button =
    "inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold " +
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed";

  return (
    <section
      aria-labelledby={titleId}
      className="relative mt-3 w-full max-w-xl rounded-lg border border-slate-300 bg-white text-slate-900 shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-slate-200 px-4 py-3">
        <h3 id={titleId} className="text-base font-semibold text-slate-950">
          {actionLabel(card.action)} · {card.quantidade} {plural}
          {agendar && card.dataPrevisao && (
            <span className="font-normal text-slate-700"> · para {formatDate(card.dataPrevisao)}</span>
          )}
        </h3>
        {aberto && (
          <p className="flex items-center gap-1.5 text-sm text-slate-700">
            <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Vale por <span className="font-semibold tabular-nums text-slate-950">{formatCountdown(left)}</span>
            </span>
          </p>
        )}
      </header>

      {/* Prévia que não vai mais acontecer fica esmaecida: o que está ali não mudou. */}
      <ul className={`divide-y divide-slate-200 ${["cancelled", "expired", "stale", "error", "unavailable"].includes(state) ? "opacity-75" : ""}`}>
        {card.itens.map((item) => {
          const final = finalPorId.get(item.id);
          return (
            <li key={item.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium text-slate-950">
                    <span className="tabular-nums text-slate-600">nº {item.id}</span> · {item.descricao || "Conta sem descrição"}
                  </p>
                  <p className="mt-0.5 break-words text-sm text-slate-700">
                    {item.fornecedor || "Fornecedor não informado"} · vence {formatDate(item.dataVencimento)}
                  </p>
                </div>
                <p className="shrink-0 font-semibold tabular-nums text-slate-950">{formatBRL(item.valorBruto)}</p>
              </div>
              <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                {agendar && <span className="text-slate-700">Previsão de pagamento:</span>}
                <span className="text-slate-700">
                  {agendar ? formatPrevisao(item.previsaoAtual) : statusLabel(item.statusValidacaoAtual)}
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                <span className="sr-only">passa para</span>
                <span className="font-semibold tabular-nums text-slate-950">
                  {agendar
                    ? formatPrevisao(final ?? item.previsaoNova)
                    : statusLabel(final ?? item.statusValidacaoNovo)}
                </span>
                {final !== undefined && <CheckCircle2 className="h-4 w-4 text-emerald-700" aria-hidden="true" />}
              </p>
            </li>
          );
        })}
      </ul>

      <div className="flex items-baseline justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
        <span className="text-sm text-slate-700">Total</span>
        <span className="text-lg font-semibold tabular-nums text-slate-950">{formatBRL(card.totalValorBruto)}</span>
      </div>

      <div ref={desfecho} tabIndex={-1} className="space-y-3 px-4 py-3 focus:outline-none">
        <p className="sr-only" aria-live="polite">{announcement}</p>

        {aberto && !notice && (
          <p className="text-sm text-slate-700">
            Nada foi alterado ainda.{" "}
            {agendar
              ? "Muda só a previsão de pagamento; o vencimento e a aprovação continuam como estão. "
              : ""}
            Confira as contas e confirme para executar.
          </p>
        )}
        {notice && (
          <p
            role="alert"
            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
              state === "stale" ? "border-amber-300 bg-amber-50 text-amber-950" : "border-red-300 bg-red-50 text-red-900"
            }`}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{notice}</span>
          </p>
        )}
        {(state === "executed" || state === "replay") && (
          <p role="status" className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-950">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {state === "replay"
                ? "Esta ação já tinha sido executada; nada foi feito de novo. A situação acima é a atual."
                : "Pronto. A situação acima é a que ficou registrada no aplicativo."}
            </span>
          </p>
        )}
        {FINAL_TEXT[state] && (
          <p role="status" className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{FINAL_TEXT[state]}</span>
          </p>
        )}

        {aberto && (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void run("confirm")}
              disabled={desabilitado}
              aria-describedby={titleId}
              className={`${button} bg-blue-700 text-white hover:bg-blue-800 focus-visible:outline-blue-700 disabled:bg-slate-400`}
            >
              {busy === "confirm" ? "Confirmando…" : confirmLabel(card.action, card.quantidade)}
            </button>
            <button
              type="button"
              onClick={() => void run("cancel")}
              disabled={desabilitado}
              className={`${button} border border-slate-400 bg-white text-slate-900 hover:bg-slate-100 focus-visible:outline-blue-700 disabled:text-slate-500`}
            >
              {busy === "cancel" ? "Cancelando…" : "Cancelar"}
            </button>
          </div>
        )}
        {(state === "stale" || state === "expired") && (
          <button
            type="button"
            onClick={() => void run("refresh")}
            disabled={desabilitado}
            className={`${button} border border-slate-400 bg-white text-slate-900 hover:bg-slate-100 focus-visible:outline-blue-700 disabled:text-slate-500`}
          >
            <RefreshCw className={`h-4 w-4 ${busy === "refresh" ? "animate-spin motion-reduce:animate-none" : ""}`} aria-hidden="true" />
            {busy === "refresh" ? "Refazendo a prévia…" : "Refazer a prévia com os dados atuais"}
          </button>
        )}
      </div>
    </section>
  );
}
